import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react'
import Button from '../common/Button'
import Icon from '../common/Icon'
import Avatar from '../common/Avatar'
import Loading from '../common/Loading'
import Logo from '../common/Logo'
import { User } from '../../context/AuthContext'
import {
  MANAGE_USERS_PERMISSION_ID,
  SEMANTICMODELS_MANAGE_PERMISSION_ID,
  hasPermission,
} from '../../constants/permissions'
import SettingsModal from '../settings/SettingsModal'
import {
  ChatOverview,
  getChatsOverview,
  deleteChat,
  renameChat,
  createCancelTokenSource,
} from '../../api/chatService'
import { CancelTokenSource } from 'axios'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  onNewChat: () => void
  onSignOut: () => void
  onChatSelect: (chatId: string) => void
  user: User
  currentChatId: string
  isNewChatRoute: boolean
  onChatCreated?: (chatId: string) => void
  onChatDeleted?: (chatId: string) => void
}

interface GroupedChats {
  today: ChatOverview[]
  yesterday: ChatOverview[]
  previous7Days: ChatOverview[]
  previous30Days: ChatOverview[]
  older: ChatOverview[]
}

const Sidebar = forwardRef<
  {
    removeEmptyChatIfExists: (chatId: string) => void
    reloadChats: () => void
  },
  SidebarProps
>(
  (
    {
      isOpen,
      onClose,
      onNewChat,
      onSignOut,
      onChatSelect,
      user,
      currentChatId,
      isNewChatRoute,
      onChatCreated,
      onChatDeleted,
    },
    ref
  ) => {
    const [chats, setChats] = useState<ChatOverview[]>([])
    const [newChatPlaceholder, setNewChatPlaceholder] =
      useState<ChatOverview | null>(null)
    const [filteredChats, setFilteredChats] = useState<ChatOverview[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [openMenuId, setOpenMenuId] = useState<string | null>(null)
    const [isRenaming, setIsRenaming] = useState<string | null>(null)
    const [renameValue, setRenameValue] = useState('')
    const [cancelTokenSource, setCancelTokenSource] =
      useState<CancelTokenSource | null>(null)
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
    const [isSettingsOpen, setIsSettingsOpen] = useState(false)
    const canManageSettings = hasPermission(
      user.permissionIds,
      SEMANTICMODELS_MANAGE_PERMISSION_ID
    )
    const canManageUsers = hasPermission(
      user.permissionIds,
      MANAGE_USERS_PERMISSION_ID
    )
    const canOpenSettings = canManageSettings || canManageUsers

    const menuRef = useRef<HTMLDivElement>(null)
    const renameInputRef = useRef<HTMLInputElement>(null)
    const userDropdownRef = useRef<HTMLDivElement>(null)
    const userButtonRef = useRef<HTMLButtonElement>(null)
    const ellipsisRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

    // This effect now manages the placeholder state more intelligently
    useEffect(() => {
      if (isNewChatRoute) {
        setNewChatPlaceholder({
          id: currentChatId,
          title: 'New Chat',
          timestamp: Date.now() / 1000,
        })
      } else {
        // Don't immediately remove placeholder when route changes
        // Only remove it if we have the real chat from server
        const realChatExists = chats.some(chat => chat.id === currentChatId)
        if (realChatExists) {
          setNewChatPlaceholder(null)
        }
        // If real chat doesn't exist yet, keep placeholder until silent reload completes
      }
    }, [isNewChatRoute, currentChatId, chats])

    // Fetch chats on component mount
    useEffect(() => {
      initialLoadChats()

      return () => {
        if (cancelTokenSource) {
          cancelTokenSource.cancel('Component unmounted')
        }
      }
    }, [])

    // Initial load with loading spinner
    const initialLoadChats = async () => {
      setIsLoading(true)
      const source = createCancelTokenSource()
      setCancelTokenSource(source)
      try {
        const chatsData = await getChatsOverview(source)
        const sortedChats = chatsData.sort((a, b) => b.timestamp - a.timestamp)
        setChats(sortedChats)
      } catch (error) {
        console.error('Error loading chats:', error)
      } finally {
        setIsLoading(false)
        setCancelTokenSource(null)
      }
    }

    // Silent reload without affecting UI
    const silentReloadChats = async () => {
      const source = createCancelTokenSource()
      try {
        const chatsData = await getChatsOverview(source)
        const sortedChats = chatsData.sort((a, b) => b.timestamp - a.timestamp)
        setChats(sortedChats)
      } catch (error) {
        console.error('Error loading chats:', error)
      }
    }

    // useMemo combines the real chats from the server and the temporary placeholder.
    const combinedChats = useMemo(() => {
      // Filter out any real chat that might have the same ID as the placeholder.
      const realChats = chats.filter(c => c.id !== newChatPlaceholder?.id)
      if (newChatPlaceholder) {
        return [newChatPlaceholder, ...realChats]
      }
      return chats
    }, [chats, newChatPlaceholder])

    // Filter chats based on search query - now uses combinedChats
    useEffect(() => {
      if (searchQuery.trim()) {
        const filtered = combinedChats.filter(chat =>
          chat.title?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        setFilteredChats(filtered)
      } else {
        setFilteredChats(combinedChats)
      }
    }, [combinedChats, searchQuery])

    // Close menu when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node

        // Handle ellipsis menu
        if (
          menuRef.current &&
          !menuRef.current.contains(target) &&
          !Object.values(ellipsisRefs.current).some(ref =>
            ref?.contains(target)
          )
        ) {
          setOpenMenuId(null)
        }

        // Handle user dropdown
        if (
          userDropdownRef.current &&
          !userDropdownRef.current.contains(target) &&
          !userButtonRef.current?.contains(target)
        ) {
          setIsUserDropdownOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    // Focus rename input when entering rename mode
    useEffect(() => {
      if (isRenaming && renameInputRef.current) {
        renameInputRef.current.focus()
        renameInputRef.current.select()
      }
    }, [isRenaming])

    const groupChatsByTime = (chats: ChatOverview[]): GroupedChats => {
      const now = new Date()

      // Get start of today (00:00:00)
      const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      )
      const todayTimestamp = startOfToday.getTime() / 1000

      // Get start of yesterday (00:00:00 yesterday)
      const startOfYesterday = new Date(startOfToday)
      startOfYesterday.setDate(startOfYesterday.getDate() - 1)
      const yesterdayTimestamp = startOfYesterday.getTime() / 1000

      // Get start of 7 days ago (00:00:00 seven days ago)
      const startOf7DaysAgo = new Date(startOfToday)
      startOf7DaysAgo.setDate(startOf7DaysAgo.getDate() - 7)
      const sevenDaysTimestamp = startOf7DaysAgo.getTime() / 1000

      // Get start of 30 days ago (00:00:00 thirty days ago)
      const startOf30DaysAgo = new Date(startOfToday)
      startOf30DaysAgo.setDate(startOf30DaysAgo.getDate() - 30)
      const thirtyDaysTimestamp = startOf30DaysAgo.getTime() / 1000

      return chats.reduce(
        (groups, chat) => {
          if (chat.timestamp >= todayTimestamp) {
            groups.today.push(chat)
          } else if (chat.timestamp >= yesterdayTimestamp) {
            groups.yesterday.push(chat)
          } else if (chat.timestamp >= sevenDaysTimestamp) {
            groups.previous7Days.push(chat)
          } else if (chat.timestamp >= thirtyDaysTimestamp) {
            groups.previous30Days.push(chat)
          } else {
            groups.older.push(chat)
          }
          return groups
        },
        {
          today: [] as ChatOverview[],
          yesterday: [] as ChatOverview[],
          previous7Days: [] as ChatOverview[],
          previous30Days: [] as ChatOverview[],
          older: [] as ChatOverview[],
        }
      )
    }

    const handleDeleteChat = async (chatId: string) => {
      try {
        await deleteChat(chatId)
        setChats(prev => prev.filter(chat => chat.id !== chatId))
        setOpenMenuId(null)
        if (onChatDeleted) {
          onChatDeleted(chatId)
        }
      } catch (error) {
        console.error('Error deleting chat:', error)
      }
    }

    const handleRenameStart = (chat: ChatOverview) => {
      setIsRenaming(chat.id)
      setRenameValue(chat.title || 'Untitled Chat')
      setOpenMenuId(null)
    }

    const handleRenameSubmit = async (chatId: string) => {
      if (renameValue.trim()) {
        try {
          await renameChat(chatId, renameValue.trim())
          setChats(prev =>
            prev.map(chat =>
              chat.id === chatId ? { ...chat, title: renameValue.trim() } : chat
            )
          )
        } catch (error) {
          console.error('Error renaming chat:', error)
        }
      }
      setIsRenaming(null)
      setRenameValue('')
    }

    const handleRenameCancel = () => {
      setIsRenaming(null)
      setRenameValue('')
    }

    const handleKeyPress = (e: React.KeyboardEvent, chatId: string) => {
      if (e.key === 'Enter') {
        handleRenameSubmit(chatId)
      } else if (e.key === 'Escape') {
        handleRenameCancel()
      }
    }

    // Remove empty new chat if no messages sent
    const removeEmptyChatIfExists = (chatId: string) => {
      setChats(prev => prev.filter(chat => chat.id !== chatId))
    }

    const handleUserDropdownToggle = (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsUserDropdownOpen(!isUserDropdownOpen)
    }

    const handleSignOut = () => {
      setIsUserDropdownOpen(false)
      onSignOut()
    }

    const clearSearch = () => {
      setSearchQuery('')
    }

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearSearch()
      }
    }

    const renderChatGroup = (title: string, chats: ChatOverview[]) => {
      if (chats.length === 0) return null

      return (
        <div className="sidebar__chat-group">
          <h3 className="sidebar__group-title">{title}</h3>
          <div className="sidebar__chat-list">
            {chats.map(chat => (
              <div key={chat.id} className="sidebar__chat-item-container">
                {isRenaming === chat.id ? (
                  <div className="sidebar__chat-rename">
                    <input
                      ref={renameInputRef}
                      type="text"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => handleKeyPress(e, chat.id)}
                      onBlur={() => handleRenameSubmit(chat.id)}
                      className="sidebar__rename-input"
                    />
                  </div>
                ) : (
                  <div
                    className={`sidebar__chat-item ${
                      currentChatId === chat.id
                        ? 'sidebar__chat-item--active'
                        : ''
                    } ${
                      chat.title === 'New Chat'
                        ? 'sidebar__chat-item--disabled'
                        : ''
                    }`}
                    onClick={() => {
                      if (chat.title !== 'New Chat') {
                        onChatSelect(chat.id)
                      }
                    }}
                    title={chat.title || 'Untitled Chat'}
                  >
                    <span className="sidebar__chat-title">
                      {chat.title || 'Untitled Chat'}
                    </span>
                    {chat.title !== 'New Chat' && (
                      <button
                        ref={el => {
                          ellipsisRefs.current[chat.id] = el
                        }}
                        className="sidebar__chat-menu-trigger"
                        onClick={e => {
                          e.stopPropagation()
                          setOpenMenuId(openMenuId === chat.id ? null : chat.id)
                        }}
                      >
                        <Icon name="ellipsis-vertical" size={14} />
                      </button>
                    )}
                  </div>
                )}

                {openMenuId === chat.id && (
                  <div ref={menuRef} className="sidebar__chat-menu">
                    {/* <button
                      className="sidebar__menu-item"
                      onClick={() => handleRenameStart(chat)}
                    >
                      <Icon name="edit" size={14} />
                      Rename
                    </button> */}
                    <button
                      className="sidebar__menu-item sidebar__menu-item--danger"
                      onClick={() => handleDeleteChat(chat.id)}
                    >
                      <Icon name="trash" size={14} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )
    }

    const groupedChats = groupChatsByTime(filteredChats)

    useImperativeHandle(ref, () => ({
      removeEmptyChatIfExists,
      reloadChats: () => silentReloadChats(),
    }))

    return (
      <>
        <aside className={`sidebar ${isOpen ? 'sidebar--open' : ''}`}>
          <div className="sidebar__header">
            <Logo
              className="sidebar__logo-container"
              logoClassName="sidebar__logo"
              textClassName="sidebar__title"
            />
          </div>

          <div className="sidebar__actions">
            <Button
              variant="primary"
              onClick={onNewChat}
              className="sidebar__new-chat-btn"
            >
              <Icon name="square-pen" size={16} />
              New chat
            </Button>
          </div>

          <div className="sidebar__search">
            <div className="sidebar__search-container">
              <Icon name="search" size={16} className="sidebar__search-icon" />
              <input
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="sidebar__search-input"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="sidebar__search-clear"
                  title="Clear search"
                >
                  <Icon name="close" size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="sidebar__content">
            {isLoading ? (
              <div className="sidebar__loading">
                <Loading />
                Loading chats...
              </div>
            ) : (
              <>
                {renderChatGroup('Today', groupedChats.today)}
                {renderChatGroup('Yesterday', groupedChats.yesterday)}
                {renderChatGroup('Previous 7 days', groupedChats.previous7Days)}
                {renderChatGroup(
                  'Previous 30 days',
                  groupedChats.previous30Days
                )}
                {renderChatGroup('Older', groupedChats.older)}

                {filteredChats.length === 0 && (
                  <div className="sidebar__empty">
                    {searchQuery ? 'No chats found' : 'No chats yet'}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="sidebar__footer">
            <div className="sidebar__user-section">
              <button
                ref={userButtonRef}
                className="sidebar__user-info"
                onClick={handleUserDropdownToggle}
              >
                <Avatar user={user} size={32} />
                <div className="sidebar__user-details">
                  <span className="sidebar__user-name">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="sidebar__user-email">{user.email}</span>
                </div>
              </button>

              {isUserDropdownOpen && (
                <div ref={userDropdownRef} className="sidebar__user-dropdown">
                  {/* <div className="sidebar__dropdown-header">
                    <Avatar user={user} size={48} />
                    <div className="sidebar__dropdown-user-details">
                      <p className="sidebar__dropdown-user-name">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="sidebar__dropdown-user-email">
                        {user.email}
                      </p>
                    </div>
                  </div> */}

                  {user.roles && user.roles.length > 0 && (
                    <div className="sidebar__dropdown-roles">
                      <p className="sidebar__dropdown-roles-text">
                        Roles:{' '}
                        {user.roles.map(role => (
                          <span className="sidebar__dropdown-role" key={role}>
                            {role}
                          </span>
                        ))}
                      </p>
                    </div>
                  )}

                  <div className="sidebar__dropdown-actions">
                    {canOpenSettings && (
                      <button
                        className="sidebar__dropdown-action"
                        onClick={() => {
                          setIsUserDropdownOpen(false)
                          setIsSettingsOpen(true)
                        }}
                      >
                        <Icon name="settings" size={16} />
                        Settings
                      </button>
                    )}
                    <button
                      className="sidebar__dropdown-action"
                      onClick={handleSignOut}
                    >
                      <Icon name="sign-out" size={16} />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>

        {isOpen && <div className="sidebar__overlay" onClick={onClose} />}

        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      </>
    )
  }
)

export default Sidebar
